import type { MilestoneDatesV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatIsoDate } from "../../../../../lib/format";
import LineItemRow from "../../../components/LineItemRow";

interface MilestoneDatesProperties {
  dates?: MilestoneDatesV2;
}

const milestoneDateFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
});

const formatMilestone = (date: string | undefined) => {
  if (!date) {
    return "-";
  }
  // Date-only values have no instant to convert; preserve their calendar date.
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/.test(date)) {
    return formatIsoDate(date);
  }
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : milestoneDateFormat.format(parsed);
};

function MilestoneDates({ dates }: MilestoneDatesProperties) {
  const { t } = useTranslation();

  const rows = [
    { key: "dealSubmitted", date: dates?.dealSubmittedDate },
    { key: "dealPackageReceived", date: dates?.dealPackageReceivedDate },
    { key: "dscReviewInitiated", date: dates?.dscReviewInitiatedDate },
    { key: "dealHeld", date: dates?.dealHeldDate },
    { key: "dealFunded", date: dates?.dealFundedDate },
  ] as const;

  const showReassignment = Boolean(dates?.reassignmentRequestedDate || dates?.reassignmentCompletedDate);

  return (
    <div className="gap-(--spacing-1) flex flex-col">
      {rows.map((row) => (
        <LineItemRow
          key={row.key}
          label={t(`dealStatus.milestones.${row.key}`)}
          value={formatMilestone(row.date)}
          labelClassName="font-(--font-weight-semibold)"
        />
      ))}
      {showReassignment && (
        <>
          <LineItemRow
            label={t("dealStatus.milestones.reassignmentRequested")}
            value={formatMilestone(dates?.reassignmentRequestedDate)}
            labelClassName="font-(--font-weight-semibold)"
          />
          <LineItemRow
            label={t("dealStatus.milestones.reassignmentCompleted")}
            value={formatMilestone(dates?.reassignmentCompletedDate)}
            labelClassName="font-(--font-weight-semibold)"
          />
        </>
      )}
    </div>
  );
}

export default MilestoneDates;
